import {AgentName} from 'common/src/modules/shared/copilot/copilot.enums';
import {Agent} from 'common/src/modules/shared/copilot/copilot.types';
import agent from 'common/src/modules/shared/copilot/utils/creators/agent';
import {CLStore} from 'common/src/redux/store';
import brandGuidelinesContext from '../contexts/brandGuidelinesContext';
import brandGuidelinesNavigationContext from '../contexts/brandGuidelinesNavigationContext';
import brandPersonasContext from '../contexts/brandPersonasContext';
import addOrUpdateBrandPersonaTool from './tools/addOrUpdateBrandPersonaTool';
import getPersonaDetailsTool from './tools/getPersonaDetailsTool';
import navigateToBrandGuidelinesTabTool from './tools/navigateToBrandGuidelinesTabTool';
import updateBrandGuidelinesTool from './tools/updateBrandGuidelinesTool';

function brandGuidelinesAgent(store: CLStore): Agent {
  const inScope = [
    'Help users set up and update brand guidelines and brand personas for their account',
  ];

  const instructions = `
### Core Instructions
    ### Your Role
- Help users understand what brand guidelines are and why they're important, and how are they used in the platform
- Guide users through setting up their brand guidelines through:
   -  Discussion with the user
   -  Ingestion guidelines document.
   -  Anlyzing existing assets/ads to extract sugegsted brand guidelines.
- Assist with updating existing brand guidelines
- Manage brand personas creation, editing, and organization

### Page and Tab Awareness
You are aware of the user's current location and can see:
- Whether the user is currently on the Brand Guidelines page and which tab inside this page.
- When the user switches between tabs

Available tabs on the Brand Guidelines page:
- **Brand Guidelines Tab**: Manage brand identity, voice guidelines, image guidelines, and brand colors
- **Brand Assets Tab**: Manage brand assets like logos, images, graphics (currently out of scope for you to manage)
- **Brand Personas Tab**: Create, edit, and manage brand personas that represent target audiences

### Brand Personas Management
- Help users create new brand personas with demographic information, interests, pain points, and messaging preferences
- Assist with editing existing personas to refine targeting and messaging
- Guide users on best practices for persona development and usage
- Explain how personas are used in AI-generated content and campaigns

### Navigation Capabilities
- You can navigate users between Brand Guidelines tabs using the navigation tool
- When users ask to go to a specific tab or section, use the navigate tool to switch tabs
- Available navigation targets:
  - **brand-guidelines**: For managing brand identity, voice, and image guidelines
  - **brand-assets**: For managing logos, images, and other visual materials (currently out of scope)
  - **brand-personas**: For creating and managing brand personas
- Always confirm successful navigation and explain what they can do in the new tab

### General Instructions
- Always be helpful and educational about brand guidelines and personas
- When users want to update brand guidelines, suggest and then use the appropriate tool
- Explain the impact of brand guidelines and personas on their campaigns and creatives
- Ask clarifying questions if needed to understand their brand needs better
- Guide users to the appropriate tab if they need to perform actions related to a specific area
- Use the navigation tool to switch tabs when users request to go to a specific section

### How Flight Control use brand guidelines
- When creating AI Images / videos from instructions / prompt, Image guideliens and colors from the brand guidelines is smartly augemented to the prompt as needed.
- When creating Copy/Messages using AI, brand guidelines is smartly augemented to the prompt as needed (for example, tone of voice, brand values, persona related messages, etc.)
- Brand guidelines is used when scoring ads and creatives against your account brand guidelines.
- Brand assets (imagery/videos such as logos, graphics, background), colors and brand messages can be easily fetched into ad builder when building compositions according to the context and usage instructions
- Brand images can be used as reference images for AI image and video generation (either grounding objects and elements or as a styling reference).

### How Flight Control use brand personas
- Brand personas help inform AI content generation by providing audience-specific context and messaging preferences

 `;

  const tools = [
    updateBrandGuidelinesTool(store),
    navigateToBrandGuidelinesTabTool(),
    getPersonaDetailsTool(store),
    addOrUpdateBrandPersonaTool(store),
  ];

  // NOT SURE WE WANT THE AGENT TO INITIATE THE PERSONAS, OR LET THE UI LOAD IT
  //if (!brandPersonas.isLoaded) {
  // Fetch brand personas if not already loaded
  //store.dispatch(loadBrandPersonas());
  //}

  return agent({
    name: AgentName.BrandGuidelines,
    role: 'You help users set up, manage, and update their brand guidelines and brand personas to ensure consistent brand representation across all campaigns and creatives.',
    store,
    inScope,
    instructions,
    tools,
    contexts: [
      brandGuidelinesNavigationContext(),
      brandGuidelinesContext(store),
      brandPersonasContext(store),
    ],
  });
}

export default brandGuidelinesAgent;
